import { rankItem } from "@tanstack/match-sorter-utils";
import {
  ColumnFiltersState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CardBody, Input, Row, Table } from "reactstrap";

interface TableContainerProps {
  columns: any;
  data: any[];
  customPageSize: number;
  isGlobalFilter?: boolean;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  totalPaginas: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalRegistros: number;
  itensPorPagina: number;
  onLimitChange?: (limit: number) => void;
  useExpanding?: boolean; // Se true, habilita expansão
  aparecerHeader?: boolean;
  aparecerPaginacao?: boolean;
}

const TableContainer: React.FC<TableContainerProps> = ({
  columns,
  data,
  customPageSize,
  isGlobalFilter,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  totalPaginas,
  currentPage,
  onPageChange,
  totalRegistros,
  itensPorPagina,
  onLimitChange,
  useExpanding = false,
  aparecerHeader,
  aparecerPaginacao,
}) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Filtro simples com rankItem
  const fuzzyFilter = (row: any, columnId: any, value: string) => {
    return rankItem(row.getValue(columnId), value).passed;
  };

  // Criação da Tabela
  const table = useReactTable({
    data,
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: {
      columnFilters,
      globalFilter,
      pagination: {
        pageIndex: currentPage,
        pageSize: itensPorPagina,
      },
      // Se habilitamos useExpanding, controlamos expanded
      expanded: useExpanding ? expanded : {},
    },
    onExpandedChange: useExpanding ? setExpanded : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: "includesString",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    pageCount: totalPaginas,
    getSubRows: useExpanding ? (row: any) => row?.subRows : undefined,
    getExpandedRowModel: useExpanding ? getExpandedRowModel() : undefined,
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  // Ajuste do tamanho de página
  useEffect(() => {
    if (customPageSize) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize]);

  // Paginação
  const pageSizeOptions = [5, 10, 20, 50, 100];
  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = Number(event.target.value);
    onLimitChange && onLimitChange(newLimit);
  };

  return (
    <Fragment>
      {/* Filtro global, caso queira */}
      {isGlobalFilter && (
        <Row className="mb-3">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            {/* Campo de pesquisa global, se necessário */}
          </CardBody>
        </Row>
      )}
      <div className={divClass} style={{ width: "100%", overflowX: "auto" }}>
        <Table
          hover
          className={tableClass}
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          {!aparecerHeader ? null : (
            <thead className={theadClass}>
              {getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={trClass}>
                  {headerGroup.headers.map((header) => {
                    // Se for a coluna "expander" e estamos usando subRows,
                    // podemos exibir um ícone para expandir/recolher tudo
                    if (
                      useExpanding &&
                      header.column.id === "expander" &&
                      headerGroup.headers.length > 1
                    ) {
                      return (
                        <th key={header.id} style={{ width: "50px" }}>
                          <button
                            className="btn btn-sm "
                            onClick={table.getToggleAllRowsExpandedHandler()}
                          >
                            {table.getIsAllRowsExpanded() ? "👇" : "👉"}
                          </button>
                        </th>
                      );
                    }
                    return (
                      <th key={header.id} className={thClass}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                {/* Linha Principal */}
                <tr>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </Table>
      </div>
      {/* Paginação */}
      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        <div className="col-sm">
          <div className="text-muted d-flex align-items-center">
            {onLimitChange === undefined ? (
              <>{totalRegistros} de registros</>
            ) : (
              <>
                <span className="me-2">Mostrar</span>
                <Input
                  type="select"
                  className="form-select w-auto"
                  value={itensPorPagina}
                  onChange={handleLimitChange}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Input>
                <span className="ms-2">
                  de <span className="fw-semibold">{totalRegistros}</span>{" "}
                  registros
                </span>
              </>
            )}
          </div>
        </div>
        {aparecerPaginacao ? (
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li
                className={
                  currentPage === 0 ? "page-item disabled" : "page-item"
                }
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={() => onPageChange(currentPage - 1)}
                >
                  Anterior
                </Link>
              </li>
              <li className="page-item">
                <div className="page-link">
                  Página {currentPage + 1} de {totalPaginas}
                </div>
              </li>
              {/*Array.from({ length: totalPaginas }, (_, i) => (
              <li key={i} className="page-item">
                <Link
                  to="#"
                  className={
                    currentPage === i ? "page-link active" : "page-link"
                  }
                  onClick={() => onPageChange(i)}
                >
                  {i + 1}
                </Link>
              </li>
            ))*/}
              <li
                className={
                  currentPage === totalPaginas - 1
                    ? "page-item disabled"
                    : "page-item"
                }
              >
                <Link
                  to="#"
                  className="page-link"
                  onClick={() => onPageChange(currentPage + 1)}
                >
                  Próximo
                </Link>
              </li>
            </ul>
          </div>
        ) : null}
      </Row>
    </Fragment>
  );
};

export default TableContainer;
